import './App.css';
import React, { useState, useEffect, useRef } from "react";
import menuLogo from './menu_logo.png';
import Papa from "papaparse";
import ChartRenderer from "./ChartRenderer";
import Fuse from "fuse.js"
import { signInWithRedirect, signOut, getCurrentUser, fetchAuthSession } from "aws-amplify/auth";


export default function DataExplorer() {
  // 🔹 State for manifest, dropdown selections, and data
  const [manifest, setManifest] = useState([]);
  const [category_display, setCategory_display] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [city, setCity] = useState("Indianapolis");
  const [data, setData] = useState([]);
  const [metadata, setMetadata] = useState(null);

  // 🔹 UX state
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState("");

  // 🔹 NEW: search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // 🔹 Analytics
  const LAMBDA_URL = "https://zosopldw7zxo6mneaqgrkvf5hu0fobhe.lambda-url.us-east-2.on.aws/";
  const sessionId = useRef(crypto.randomUUID());

  const getBrowserInfo = () => {
    const ua = navigator.userAgent;
    const browsers = [
      { name: "Edge",    pattern: /Edg\/(\d+)/ },
      { name: "Chrome",  pattern: /Chrome\/(\d+)/ },
      { name: "Firefox", pattern: /Firefox\/(\d+)/ },
      { name: "Safari",  pattern: /Version\/(\d+).*Safari/ },
    ];
    for (const { name, pattern } of browsers) {
      const match = ua.match(pattern);
      if (match) return `${name} ${match[1]}`;
    }
    return ua;
  };

  const sendAnalytics = (eventType, query, source) => {
    const payload = {
      event_type: eventType,
      query,
      source,
      timestamp: new Date().toISOString(),
      session_id: sessionId.current,
      page: "chart-book",
      browser: getBrowserInfo(),
      viewport: { width: window.innerWidth, height: window.innerHeight },
    };
    fetch(LAMBDA_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch((err) => console.warn("Analytics error:", err));
  };
  
	const VISIT_LAMBDA_URL = "https://nfp2kicw3qciilehzystk3k4qa0thmyp.lambda-url.us-east-2.on.aws/";
	const visitLogged = useRef(false);

	const logVisit = async (currentUser) => {
	  if (visitLogged.current) return;       // already logged this session
	  visitLogged.current = true;

	  // Geo lookup — free, no API key needed
	  let city = "unknown", region = "unknown", country = "unknown";
	  try {
		const geo = await fetch("https://ipapi.co/json/").then(r => r.json());
		city    = geo.city    || "unknown";
		region  = geo.region  || "unknown";
		country = geo.country || "unknown";
	  } catch { /* geo failed — still log the visit, just without location */ }

	  // Bot detection via user agent
	  const ua = navigator.userAgent;
	  const is_bot = /bot|crawl|spider|slurp|facebookexternalhit|preview|headless/i.test(ua);

	  // Is this you?
	  const session = await fetchAuthSession();
	  const MY_EMAIL = "kylewvail@gmail.com";   // 🔧 replace with your Cognito email
	  const user_email = session?.tokens?.idToken?.payload?.email ?? null;
	  const is_me = user_email === MY_EMAIL;

	  fetch(VISIT_LAMBDA_URL, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
		  session_id: sessionId.current,
		  page:       "chart-book",
		  browser:    getBrowserInfo(),
		  viewport:   { width: window.innerWidth, height: window.innerHeight },
		  city,
		  region,
		  country,
		  is_bot,
		  is_me,
		}),
	  }).catch(err => console.warn("Visit log error:", err));
	};

  // 🔹 URL slug from query param (e.g. ?chart=industry-by-sex-marion-county)
  const urlSlug = new URLSearchParams(window.location.search).get("chart");

  // 🔹 Base URL for CloudFront
  const cdn_base_url = "https://d1vpu7qpoa3nub.cloudfront.net/";

  const [user, setUser] = useState(null);
  const [userGivenName, setUserGivenName] = useState("");

	useEffect(() => {
	  async function loadUser() {
		try {
		  const u = await getCurrentUser();
		  const session = await fetchAuthSession();
		  console.log("ID token payload:", session?.tokens?.idToken?.payload);
		  const givenName = session?.tokens?.idToken?.payload?.given_name ?? "";
		  setUser(u);
		  setUserGivenName(givenName);
		  logVisit(u);
		} catch {
		  setUser(null);
		  setUserGivenName("");
		  logVisit(null);
		}
	  }
	  loadUser();
	}, []);


  const handleLogin = async () => {
    await signInWithRedirect();
  };

  const handleLogout = async () => {
    await signOut({ global: true });
    setUser(null);
    setUserGivenName("");
  };

	const handleDownload = () => {
	  if (!data || data.length === 0) return;
	  const csv = Papa.unparse(data);
	  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
	  const url = URL.createObjectURL(blob);
	  const a = document.createElement("a");
	  a.href = url;
	  a.download = "data.csv";
	  document.body.appendChild(a);
	  a.click();
	  a.remove();
	  URL.revokeObjectURL(url);
	};

  
  const formatMetadataDate = (value, { withDay }) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);

    const options = withDay
      ? { month: "short", day: "2-digit", year: "numeric" }
      : { month: "short", year: "numeric" };

    return new Intl.DateTimeFormat("en-US", options).format(parsed);
  };

  const hasDayInDate = (value) => {
    if (!value) return false;
    return (
      /\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(value) ||
      /\d{1,2}[-/]\d{1,2}[-/]\d{2,4}/.test(value) ||
      /[A-Za-z]+\s+\d{1,2},?\s+\d{4}/.test(value)
    );
  };

  const updatedDate = metadata?.updated
    ? formatMetadataDate(metadata.updated, { withDay: true })
    : "";
  const maxObsDate = metadata?.max_obs_display || "";


  // ----------------------------------------------------------------
  // 1) Load manifest.json once when component mounts
  // ----------------------------------------------------------------
  useEffect(() => {
    fetch(cdn_base_url + "reference/data_manifest.json")
      .then((res) => {
        if (!res.ok) throw new Error(`Manifest HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        setManifest(Array.isArray(json) ? json : []);
        setError("");
      })
      .catch((err) => {
        console.error("Error loading manifest:", err);
        setError("Failed to load manifest.");
      });
  }, []);

  // ----------------------------------------------------------------
  // 2) Auto-load chart from URL slug on manifest load
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!manifest.length || !urlSlug) return;
    const match = manifest.find((d) => d.url_slug === urlSlug);
    if (match) {
      setCategory_display(match.category_display);
      setSubcategory(match.subcategory);
      setCity(match.city);
    }
  }, [manifest]);

  // ----------------------------------------------------------------
  // 3) NEW: Filter manifest entries by searchTerm
  // ----------------------------------------------------------------
  useEffect(() => {
  if (!searchTerm || !manifest.length) {
    setSearchResults([]);
    return;
  }

  const options = {
    keys: ["category_display", "subcategory", "tags", "city"],
    threshold: 0.35, // 0 = exact match, 1 = anything
    includeScore: true,
  };

  const fuse = new Fuse(manifest, options);
  const results = fuse.search(searchTerm);

  const topMatches = results
    .sort((a, b) => a.score - b.score)
    .slice(0, 8)
    .map(r => r.item);

  setSearchResults(topMatches);
}, [searchTerm, manifest]);


  // ----------------------------------------------------------------
  // 4) Load data.csv (and meta.json) when filters change
  // ----------------------------------------------------------------

    const selected = React.useMemo(() => {
	  if (!Array.isArray(manifest)) return null;
	  return manifest.find(
	    (d) =>
		  d.category_display === category_display &&
		  d.subcategory === subcategory &&
		  d.city === city
	  );
  }, [manifest, category_display, subcategory, city]);


// ✅ 1st effect: fetch CSV whenever selected dataset changes
    useEffect(() => {
	  if (!selected?.s3_key) return;

	  const csvUrl = cdn_base_url + selected.s3_key;
	  setLoadingData(true);    
	  setError(null);

      let isCurrent = true; // prevents old responses overwriting

    
    const lastSlash = selected.s3_key.lastIndexOf("/");
    const dir = selected.s3_key.slice(0, lastSlash + 1);
    const metaUrl = cdn_base_url + dir + "meta.json";

    setLoadingData(true);
    setError(null);
	setMetadata(null);  // ✅ clear previous meta so old title/citation don't linger


    fetch(csvUrl)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        return text;
      })
      .then((text) => {
        if (typeof text !== "string") {
          throw new Error("CSV fetch did not return text (unexpected type).");
        }

        const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
        const toNum = (v) => {
          if (v === null || v === undefined) return NaN;
          const n = Number(String(v).trim());
          return Number.isFinite(n) ? n : NaN;
        };

        const chartType = selected?.chart_type || "time_series";
        let formatted = [];

        if (selected?.chart_type === "table") {
          const clean = parsed.data.map((row) =>
            Object.fromEntries(
              Object.entries(row).map(([k, v]) => [
                k.replace(/\r?\n/g, " ").trim(),
                v,
              ])
            )
          );
          setData(clean);
          setLoadingData(false);
          return;
        }

        if (chartType === "combo_cy_hist_marker") {
          formatted = parsed.data
            .map((row) => ({
              month: (row["month"] || row["Month"] || "").trim(),
              cy: toNum(row["value_cy"] ?? row["cy"]),
              hist: toNum(row["value_hist"] ?? row["hist"]),
            }))
            .filter(
              (d) => d.month && (!Number.isNaN(d.cy) || !Number.isNaN(d.hist))
            );
        
		} else if (chartType === "stacked_bar") {
		  formatted = parsed.data
			.map((row) => ({
			  series: (row["series"] || row["Series"] || "").trim(),
			  x_axis: (row["x_axis"] || row["X_axis"] || row["X"] || "").trim(),
			  value: toNum(row["value"] ?? row["Value"]),
			}))
			.filter((d) => d.series && d.x_axis && !Number.isNaN(d.value));
			
			  // 👀 Debug: show what was parsed before setting state
			  console.log("Stacked Bar parsed sample:", formatted.slice(0, 10));
			  console.log(
				"Unique series:",
				[...new Set(formatted.map((d) => d.series))],
				"Unique x_axis:",
				[...new Set(formatted.map((d) => d.x_axis))]
			  );

		
		} else if (chartType === "percent_bar_w_total") {
		  formatted = parsed.data
			.map((row) => {
			  const rawRowType = (row["row_type"] || row["Row_type"] || "").trim();
			  return {
				row_type: rawRowType.toLowerCase(),
				label_group_clean: (row["label_group_clean"] || row["Label_group_clean"] || "").trim(),
				characteristic: (row["characteristic"] || row["Characteristic"] || "").trim(),
				value: toNum(row["value"] ?? row["Value"]),
			  };
			})
			.filter(
			  (d) =>
				d.row_type &&
				d.characteristic &&
				((d.row_type === "total" && !Number.isNaN(d.value)) ||
				  (d.row_type === "category" &&
					d.label_group_clean &&
					!Number.isNaN(d.value)))
			);

		} else {
          formatted = parsed.data
            .map((row) => ({
              date: (row["date_obs"] || row["date"] || "").trim(),
              value: toNum(row["value"] ?? row["val"]),
            }))
            .filter((d) => d.date && !Number.isNaN(d.value));
        }

        if (isCurrent) setData(formatted); // ✅ safe set
      })
      .catch((err) => {
        console.error("CSV parse/fetch error:", err);
        setError(err.message || "Failed to load CSV");
        setData([]);
      })
      .finally(() => setLoadingData(false));
	  return () => { isCurrent = false }; // ✅ cancel on dependency change
   }, [selected?.s3_key, selected?.chart_type, cdn_base_url]);  // ✅ new deps

 // ✅ 2nd effect: fetch metadata separately
   useEffect(() => {
     if (!selected?.s3_key) return;

     const lastSlash = selected.s3_key.lastIndexOf("/");
     const dir = selected.s3_key.slice(0, lastSlash + 1);
     const metaUrl = cdn_base_url + dir + "meta.json";

     setMetadata(null); // clears stale title/citation
     let isCurrent = true;

     fetch(metaUrl)
       .then((res) => (res.ok ? res.json() : null))
       .then((json) => {
         if (!isCurrent) return;
         console.log("Loaded metadata for:", metaUrl, json);
         setMetadata(json);
       })
       .catch((err) => {
        if (!isCurrent) return;
        console.warn("Meta fetch failed:", err);
         setMetadata(null);
       });

     return () => { isCurrent = false };
   }, [selected?.s3_key, cdn_base_url]);

  // ----------------------------------------------------------------
  // 4) Dropdown option derivations
  // ----------------------------------------------------------------
  const categories = Array.isArray(manifest)
    ? [...new Set(manifest.map((d) => d.category_display))].sort()
    : [];

  const subcategories =
    category_display && Array.isArray(manifest)
      ? [
          ...new Set(
            manifest
              .filter((d) => d.category_display === category_display)
              .map((d) => d.subcategory)
          ),
        ].sort()
      : [];



  // ----------------------------------------------------------------
  // 5) Render UI
  // ----------------------------------------------------------------
  return (
    <>
	<header className="ccd-app-header" role="banner">
	  <div className="ccd-header-inner">
		<a
		  href="https://circlecitydata.com"
		  className="ccd-logo-link"
		  aria-label="Circle City Data home"
		>
		  <div className="ccd-logo-block">
			<div className="ccd-logo-text-group">
			  <span className="ccd-logo-supertitle">Circle City Data</span>
			  <span className="ccd-logo-title">Chart Book</span>
			</div>
			<img src={menuLogo} alt="" className="ccd-logo-img" />
		  </div>
		</a>

		<div className="ccd-auth-controls">
		  {!user ? (
			<button className="ccd-auth-button" onClick={handleLogin}>Log in or sign up</button>
		  ) : (
			<>
			  <span className="ccd-user">
				{userGivenName ? `Welcome, ${userGivenName}` : "Welcome"}
			  </span>
			  <button onClick={handleLogout}>Log out</button>
			</>
		  )}
		</div>
	  </div>
	</header>


      <div className="app-container">
        <div className="card">
          <div className="filter-row">
            {/* ✅ NEW: Search bar */}
            <div className="search-bar">
              <div className="search-label-row">
                <label className="dropdown-label" htmlFor="dataset-search">
                  Search
                </label>
                <span className="search-hint">
                  Not sure what you're looking for? Browse our{" "}
                  <a
                    href="https://charts.circlecitydata.com/chart_index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="search-hint-link"
                  >
                    interactive data catalog
                  </a>{" "}
                  for available data.
                </span>
              </div>
              <input
                id="dataset-search"
                type="text"
                placeholder="Search datasets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchTerm.trim()) {
                    sendAnalytics("search_confirmed", searchTerm.trim(), "manual_submit");
                  }
                }}
                onBlur={() => {
                  if (searchTerm.trim()) {
                    sendAnalytics("search_abandoned", searchTerm.trim(), "blur");
                  }
                }}
                className="search-input"
              />

              {searchResults.length > 0 && (
                <ul className="search-suggestions">
                  {searchResults.map((r, idx) => (
                    <li
                      key={idx}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        sendAnalytics("search_confirmed", searchTerm.trim(), "suggestion_click");
                        setCategory_display(r.category_display);
                        setSubcategory(r.subcategory);
                        setSearchTerm("");
                        setSearchResults([]);
                      }}
                    >
                      <strong>{r.subcategory}</strong> — {r.category_display}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Existing dropdowns below */}
            <div className="dataset-selector">
              <div className="dropdown-group">
                <label className="dropdown-label">Category</label>
                <select
                  value={category_display}
                  onChange={(e) => {
                    setCategory_display(e.target.value);
                    setSubcategory("");
                  }}
                  className="dropdown"
                >
                  <option value="">-- Select Category --</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="dropdown-group">
                <label className="dropdown-label">Dataset</label>
                <select
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                  disabled={!category_display}
                  className="dropdown"
                >
                  <option value="">-- Select Dataset --</option>
                  {subcategories.map((sub) => (
                    <option key={sub} value={sub}>
                      {sub}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Status + Chart rendering (unchanged) */}
          <div className="status-line">
            {error && <span className="status-error">{error}</span>}
            {!error && loadingData && (
              <span className="status-loading">Loading dataset…</span>
            )}
            {!error &&
              !loadingData &&
              category_display &&
              subcategory &&
              city &&
              data.length === 0 && (
                <span className="status-empty">No rows after parsing.</span>
              )}
          </div>

          {metadata && (
            <div className="chart-title">
              <h2>{metadata.title}</h2>
            </div>
		  )}
		  
		  {(updatedDate || maxObsDate) && (
		    <div className="chart-date-group">
			  {updatedDate && (
			    <div className="chart-date-updated">
				  Data Updated: {updatedDate}
				  <span
				    className="info-icon"
				    title="Data Updated represents the most recent date that facts were known to be true or updated."
				    aria-label="Data Updated represents the most recent date that facts were known to be true or updated."
				  >
				    ⓘ
				  </span>
			    </div>
			  )}
			  {maxObsDate && (
			    <div className="chart-date-updated">
				  Latest Observation: {maxObsDate}
			    </div>
			  )}
		    </div>
		  )}



          <div className="chart-container">
            {data.length > 0 ? (
              <ChartRenderer
                selected={selected}
                data={data}
                metadata={metadata}
              />
            ) : !loadingData && category_display && subcategory ? (
              <div className="chart-placeholder">No data available</div>
            ) : !loadingData && !category_display && !subcategory ? (
              <p className="chart-empty-prompt">
                View charts by selecting a Category and Dataset
              </p>
            ) : null}
          </div>

          {/* Download button — shown whenever a dataset is selected */}
          {selected?.s3_key && (
            <div className="download-row">
              <button
                className={`download-btn${user ? "" : " download-btn--disabled"}`}
                onClick={user ? handleDownload : undefined}
                disabled={!user}
                title={user ? "Download CSV" : "Log in or sign up to download data"}
                aria-disabled={!user}
              >
                ⬇ Download Data
              </button>
              {!user && (
                <p className="download-login-hint">
                  Account required.{" "}
                  <button className="download-login-link" onClick={handleLogin}>
                    Log in or sign up here
                  </button>
                </p>
              )}
            </div>
          )}

          {metadata && (
            <div className="metadata-card">
              <p className="metadata-description">{metadata.description}</p>
              {metadata.citation && (
                <p className="metadata-citation">
                  Source: {metadata.citation}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <footer className="ccd-footer" role="contentinfo">
        <div className="ccd-footer-inner">
          <p>
            © {new Date().getFullYear()} Circle City Data. All rights reserved.
          </p>
        </div>
      </footer>
    </>
  );
}
